import { type FieldValues, type Path, type UseFormReturn } from 'react-hook-form';
import { Input } from '@/components/ui/input';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/cn';

interface FormFieldProps<T extends FieldValues> {
  form: UseFormReturn<T>;
  name: Path<T>;
  label: string;
  /**
   * Any native input type, plus `decimal` for amounts and percentages — see
   * DecimalInput for why those must not be `number`.
   */
  type?: string;
  placeholder?: string;
  description?: string;
  className?: string;
  disabled?: boolean;
  required?: boolean;
}

export function FormField<T extends FieldValues>({
  form,
  name,
  label,
  type = 'text',
  placeholder,
  description,
  className,
  disabled,
  required,
}: FormFieldProps<T>) {
  const error = form.formState.errors[name];

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={name}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      {type === 'decimal' ? (
        <DecimalInput
          id={name}
          placeholder={placeholder}
          disabled={disabled}
          {...form.register(name)}
        />
      ) : (
        <Input
          id={name}
          type={type}
          placeholder={placeholder}
          disabled={disabled}
          {...form.register(name)}
        />
      )}
      {description && !error && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {error && (
        <p className="text-xs text-destructive">{error.message as string}</p>
      )}
    </div>
  );
}
